import { EFeatureNames } from '@albacross/types'

import type { FeatureGroupId } from './featureGroups'

export type FeatureKind = 'flag' | 'limit' | 'text'

export type FeatureSpec = {
  label: string
  // What the customer gets if this is on, for the tooltip CS and Sales read before flipping it.
  description: string
  // Declared, never inferred: a flag the plan omits and nobody stored is undefined on both sides,
  // and guessing from that renders it as a number field. Only consulted where editable is true, so
  // a read-only entry's kind decides nothing and is not load-bearing.
  kind: FeatureKind
  group: FeatureGroupId
  editable: boolean
}

// One entry per feature, so adding one is one line. It was four lists kept in step by a test.
export const FEATURES: Record<string, FeatureSpec> = {
  // limits — Intent Companies first: it is the one people come here to read
  [EFeatureNames.LeadsLimit]: {
    label: 'Intent Companies limit',
    description:
      'Caps identified companies per month. Over it, connectors stop receiving new companies. 500000 means unlimited.',
    kind: 'limit',
    group: 'limits',
    editable: true,
  },
  [EFeatureNames.DomainsLimit]: {
    label: 'Domains limit',
    description: 'Caps tracked website domains. Also a switch: only a value above 1 shows the Domains page at all.',
    kind: 'limit',
    group: 'limits',
    editable: true,
  },
  [EFeatureNames.BomboraCompanies]: {
    label: 'Bombora companies',
    description: 'Weekly credit allowance for Signals reports. At zero, new reports and their exports are refused.',
    kind: 'limit',
    group: 'limits',
    editable: true,
  },
  [EFeatureNames.OutreachSenders]: {
    label: 'Auto-engage licenses',
    description:
      'Caps how many users can hold an Auto-Engage sender licence. Cannot be cut below the number already assigned.',
    kind: 'limit',
    group: 'limits',
    editable: true,
  },

  // contacts
  [EFeatureNames.ContactsProvider]: {
    label: 'Contacts Provider',
    description:
      'Chooses the contact data source. None gives them no contacts at all; waterfall also returns phone numbers.',
    kind: 'text',
    group: 'contacts',
    editable: true,
  },
  [EFeatureNames.ContactsUsageLimitSoft]: {
    label: 'Contacts Usage limit SOFT',
    description: 'Lets contact reveals and exports carry on past the email contact limit instead of being blocked.',
    kind: 'flag',
    group: 'contacts',
    editable: true,
  },
  [EFeatureNames.ContactsUsageLimit]: {
    label: 'Email Contact limit',
    description: 'Monthly cap on email contacts revealed or exported. Past it, requests are refused.',
    kind: 'limit',
    group: 'contacts',
    editable: true,
  },
  [EFeatureNames.ContactsPhoneNumbersLimit]: {
    label: 'Phone Contact limit',
    description: 'Monthly cap on phone numbers. Contacts past the cap still arrive, just without a number.',
    kind: 'limit',
    group: 'contacts',
    editable: true,
  },

  // products
  [EFeatureNames.RevealApi]: {
    label: 'API keys page',
    description:
      'Shows the API Keys page with their Reveal and Enrich keys and usage. Does not stop a live key from working.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.AppSecuritySettings]: {
    label: 'App Security Settings',
    description: 'Adds the Security page, where they can require two-factor login for all their users.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.Outreach]: {
    label: 'Auto-engage',
    description:
      'Turns on Auto-Engage: the outreach app, its connector type and sender licences. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.Abm]: {
    label: 'ABM',
    description: 'Turns on the ABM tab: ad campaigns, target lists and advertise-to-company actions.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.Support]: {
    label: 'Customer Success',
    description:
      'Nothing in the app reads it, so it gates nothing. Plans do set it deliberately, so treat it as a record.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.ExportsCSV]: {
    label: 'Manual CSV/Excel Export',
    description: 'Adds the CSV and Excel download options on lead lists and company profiles.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.ExportsWorkflow]: {
    label: 'Manual Connector Export',
    description: 'Adds Export to Connector, pushing the list they are looking at into a connector on demand.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.MyProspects]: {
    label: 'My Prospects',
    description: 'Shows the My Prospects tab inside Auto-Engage.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.ProfileActivity]: {
    label: 'Profile activity',
    description: 'Opens the company profile with its Activity and Signals tabs and visit panels.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.Prospector]: {
    label: 'Prospector',
    description: 'Shows the Prospector contact-search tab inside Auto-Engage.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.RoiDashboard]: {
    label: 'ROI dashboard',
    description: 'Adds the ROI panel on the home screen and its revenue summary.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.Roles]: {
    label: 'Roles',
    description: 'Lets them create and edit their own user roles instead of only using the built-in ones.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.Segments]: {
    label: 'Segments & Filters',
    description: 'Saved segments, the Tags and Website Pages pages, and most of the advanced lead filters.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },
  [EFeatureNames.Signals]: {
    label: 'Signals',
    description: 'Turns on Signals: third-party Bombora intent reports, their topics and exports.',
    kind: 'flag',
    group: 'products',
    editable: true,
  },

  // connectors
  [EFeatureNames.WorkflowDelay]: {
    label: 'Delay connector sends',
    description: 'Adds the delay option on CRM and Auto-Engage connectors, so sends wait a set time.',
    kind: 'flag',
    group: 'connectorBehaviour',
    editable: true,
  },
  [EFeatureNames.WorkflowEmail]: {
    label: 'Email Report',
    description:
      'Offers the scheduled Email Report connector, which mails matching companies to their users. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowEmailCsv]: {
    label: 'Email Report file attachments',
    description: 'Lets the Email Report attach the companies as a CSV or Excel file.',
    kind: 'flag',
    group: 'connectorBehaviour',
    editable: true,
  },
  [EFeatureNames.WorkflowManualApproval]: {
    label: 'Manual approval',
    description: 'Does nothing. Nothing in the app or in Auto-Engage reads it.',
    kind: 'flag',
    group: 'retired',
    editable: true,
  },

  // connectors
  [EFeatureNames.WorkflowAttio]: {
    label: 'Attio',
    description:
      'Offers the Attio connector, pushing matching companies and contacts into Attio. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowClay]: {
    label: 'Clay',
    description:
      'Offers the Clay connector, sending matching companies to a Clay webhook. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowGoogleSheets]: {
    label: 'Google Sheets',
    description:
      'Offers the Google Sheets connector, appending matching companies to a sheet. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowHubspot]: {
    label: 'HubSpot',
    description:
      'Offers the HubSpot connector and its connection, for syncing companies to HubSpot. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowLinkedin]: {
    label: 'LinkedIn',
    description:
      'Offers the LinkedIn Matched Audiences connector, for retargeting matched companies. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowMicrosoftTeams]: {
    label: 'Microsoft Teams',
    description:
      'Offers the Microsoft Teams connector, posting matching companies into a channel. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowN8n]: {
    label: 'n8n',
    description:
      'Offers the n8n connector and the n8n API key used by the Albacross n8n node. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowPipedrive]: {
    label: 'Pipedrive',
    description:
      'Offers the Pipedrive connector and its connection, for syncing companies to Pipedrive. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowSalesforce]: {
    label: 'Salesforce',
    description:
      'Offers the Salesforce connector and its connection, for syncing companies to Salesforce. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowSlack]: {
    label: 'Slack',
    description:
      'Offers the Slack connector, posting matching companies into a Slack channel. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowWebhook]: {
    label: 'Webhook',
    description:
      'Offers the Webhook connector, posting matching companies to a URL they choose. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },
  [EFeatureNames.WorkflowZapier]: {
    label: 'Zapier',
    description:
      'Offers the Zapier connector and the Zapier API key on their integrations page. Turning it off also stops their existing connectors of this type.',
    kind: 'flag',
    group: 'connectors',
    editable: true,
  },

  // retired

  'ai.page.intent.level': {
    label: 'AI page intent level',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'limit',
    group: 'retired',
    editable: false,
  },
  'exports.limit': {
    label: 'Exports limit',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'limit',
    group: 'retired',
    editable: false,
  },
  'homeScreen.default': {
    label: 'Home screen default',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'flag',
    group: 'retired',
    editable: false,
  },
  'users.limit': {
    label: 'Users limit',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'limit',
    group: 'retired',
    editable: false,
  },
  'workflow.advancedConditions': {
    label: 'Advanced Conditions',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'flag',
    group: 'retired',
    editable: false,
  },
  'workflow.salesforce.sync': {
    label: 'Salesforce sync',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'flag',
    group: 'retired',
    editable: false,
  },
  'workflow.salesforce.sync.custom': {
    label: 'Salesforce custom sync',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'flag',
    group: 'retired',
    editable: false,
  },
  homeScreen: {
    label: 'Home screen',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'flag',
    group: 'retired',
    editable: false,
  },
  activeUsers: {
    label: 'Active users',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'limit',
    group: 'retired',
    editable: false,
  },
  hgInsights: {
    label: 'HG Insights',
    description: 'Nothing reads this any more. It is listed only because it is still stored on some customers.',
    kind: 'flag',
    group: 'retired',
    editable: false,
  },
}
